import { Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable } from 'rxjs';

export type CookieConsentStatus = 'unset' | 'granted' | 'denied';

const STORAGE_KEY = 'askvo_analytics_consent';

/**
 * GDPR consent state for non-essential (analytics) cookies — currently just
 * Google Analytics, see GoogleAnalyticsService. Deliberately NOT scoped to
 * "EU visitors only": there's no reliable client-side way to geo-fence, and
 * asking every visitor is the safer default. The auth token / widget visitor
 * ID (see token-storage.ts) are strictly necessary and unaffected by this —
 * only opt-in analytics cookies are gated.
 *
 * Exposes both a signal (`status`, for component templates — e.g. the
 * consent banner's `@if`) and a BehaviorSubject (`status$`, for services like
 * GoogleAnalyticsService) so a service can react to changes with a plain,
 * synchronous subscription instead of an `effect()` — `effect()` is meant
 * for template-adjacent work and only flushes on Angular's own change
 * detection/microtask schedule, which is unnecessary indirection here.
 */
@Injectable({ providedIn: 'root' })
export class CookieConsentService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  private readonly statusSubject = new BehaviorSubject<CookieConsentStatus>(this.readStoredStatus());
  readonly status$: Observable<CookieConsentStatus> = this.statusSubject.asObservable();
  readonly status = signal<CookieConsentStatus>(this.statusSubject.value);

  private readStoredStatus(): CookieConsentStatus {
    if (!this.isBrowser) return 'unset';
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'granted' || stored === 'denied' ? stored : 'unset';
  }

  grant(): void {
    this.setStatus('granted');
  }

  deny(): void {
    this.setStatus('denied');
  }

  /** Reopens the banner (footer "Cookie preferences" link) so a visitor can change their mind later. */
  reset(): void {
    this.statusSubject.next('unset');
    this.status.set('unset');
    if (this.isBrowser) localStorage.removeItem(STORAGE_KEY);
  }

  private setStatus(value: 'granted' | 'denied'): void {
    this.statusSubject.next(value);
    this.status.set(value);
    if (this.isBrowser) localStorage.setItem(STORAGE_KEY, value);
  }
}
